import React from 'react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '@/components/ui';

import { GeneralSection } from './components/GeneralSection';
import { SecuritySection } from './components/SecuritySection';
import { LocalizationSection } from './components/LocalizationSection';
import { InfraSection } from './components/InfraSection';
import { AdvancedSection } from './components/AdvancedSection';
import { useSystemSettings } from './hooks/useSystemSettings';

const SECTIONS = [
  { id: 'general', label: 'Cài đặt chung', icon: 'ri-settings-4-line' },
  { id: 'security', label: 'Bảo mật', icon: 'ri-shield-keyhole-line' },
  { id: 'localization', label: 'Vùng miền', icon: 'ri-global-line' },
  { id: 'infrastructure', label: 'Hạ tầng', icon: 'ri-hard-drive-2-line' },
  { id: 'advanced', label: 'Nâng cao', icon: 'ri-flask-line' },
];

export default function AdminSettingsPage() {
  const [dirtyForms, setDirtyForms] = React.useState<Record<string, boolean>>({});
  const { isUpdating } = useSystemSettings();

  const hasChanges = Object.values(dirtyForms).some(v => v);

  const handleDirtyChange = React.useCallback((id: string, isDirty: boolean) => {
    setDirtyForms(prev => {
      if (prev[id] === isDirty) return prev;
      return { ...prev, [id]: isDirty };
    });
  }, []);

  return (
    <PageLayout
      title="Cấu hình Hệ thống"
      description="Quản lý các thiết lập toàn cục của Landscape SaaS"
      badge={
        <Badge variant="custom" className="text-slate-500 border-slate-200">
          GLOBAL CONFIG
        </Badge>
      }
    >
      <div className="flex flex-col lg:flex-row gap-8 items-start relative">
        {/* Sticky Sidebar Navigation */}
        <nav className="hidden lg:block w-64 sticky top-[80px] space-y-1 bg-white p-4 rounded-3xl shadow-sm border border-slate-100">
          <p className="px-4 text-[10px] font-black text-slate-400  tracking-widest mb-3">Mục lục</p>
          {SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-all group"
            >
              <i className={`${section.icon} text-lg text-slate-300 group-hover:text-brand-600`} />
              {section.label}
            </a>
          ))}
        </nav>

        {/* Content Sections */}
        <div className="flex-1 space-y-8 pb-32 w-full">
          {SECTIONS.map((section) => (
            <section id={section.id} key={section.id} className="scroll-mt-[100px]">
              <Card className="border-none shadow-sm overflow-hidden">
                <CardHeader className="bg-slate-50/50 border-b border-slate-100 py-4 px-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-brand-600">
                      <i className={`${section.icon} text-xl`} />
                    </div>
                    <CardTitle className="text-lg font-black text-slate-900 tracking-tight">
                      {section.label.toUpperCase()}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-8">
                  {section.id === 'general' && (
                    <GeneralSection
                      onChange={(isDirty) => handleDirtyChange('general', isDirty)}
                      onSaveSuccess={() => handleDirtyChange('general', false)}
                    />
                  )}
                  {section.id === 'security' && (
                    <SecuritySection
                      onChange={(isDirty) => handleDirtyChange('security', isDirty)}
                      onSaveSuccess={() => handleDirtyChange('security', false)}
                    />
                  )}
                  {section.id === 'localization' && (
                    <LocalizationSection
                      onChange={(isDirty) => handleDirtyChange('localization', isDirty)}
                      onSaveSuccess={() => handleDirtyChange('localization', false)}
                    />
                  )}
                  {section.id === 'infrastructure' && (
                    <InfraSection
                      onChange={(isDirty) => handleDirtyChange('infrastructure', isDirty)}
                      onSaveSuccess={() => handleDirtyChange('infrastructure', false)}
                    />
                  )}
                  {section.id === 'advanced' && (
                    <AdvancedSection
                      onChange={(isDirty) => handleDirtyChange('advanced', isDirty)}
                      onSaveSuccess={() => handleDirtyChange('advanced', false)}
                    />
                  )}
                  {section.id !== 'general' && section.id !== 'security' && section.id !== 'localization' && section.id !== 'infrastructure' && section.id !== 'advanced' && (
                    <div className="py-12 text-center">
                      <i className={`${section.icon} text-5xl text-slate-100 mb-4 block`} />
                      <h3 className="text-slate-900 font-bold mb-1">Cấu hình {section.label}</h3>
                      <p className="text-sm text-slate-400 max-w-sm mx-auto">
                        Phân hệ đang được thiết lập. Vui lòng quay lại sau để cấu hình các thông số chi tiết.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>
          ))}
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 lg:left-[calc(50%+130px)] z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="bg-slate-900/90 backdrop-blur-xl px-6 py-3 rounded-2xl shadow-2xl border border-white/10 flex items-center gap-6">
            <p className="text-xs text-slate-400 font-medium hidden sm:block">Bạn có thay đổi chưa lưu</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setDirtyForms({});
                  window.location.reload();
                }}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-bold text-slate-400 hover:text-white transition-colors disabled:opacity-50"
              >
                Hủy bỏ
              </button>
              <button
                onClick={() => {
                  // Submit tất cả các form đang dirty
                  if (dirtyForms['general']) {
                    document.getElementById('general-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                  if (dirtyForms['security']) {
                    document.getElementById('security-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                  if (dirtyForms['localization']) {
                    document.getElementById('localization-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                  if (dirtyForms['infrastructure']) {
                    document.getElementById('infra-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                  if (dirtyForms['advanced']) {
                    document.getElementById('advanced-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                }}
                disabled={isUpdating}
                className="px-6 py-2 bg-brand-600 hover:bg-brand-500 text-white text-sm font-bold rounded-xl shadow-lg shadow-brand-600/20 transition-all disabled:opacity-50 min-w-[140px]"
              >
                {isUpdating ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Đang lưu...
                  </span>
                ) : 'Lưu cấu hình'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
